// src/pages/analytics/ArInsightsTab.tsx
//
// A/R Insights — two stacked panels:
//   1. A/R Aging History (recipe: ar-history) — the five aging buckets over
//      time, built from the agent's daily snapshots. Sparse until the agent
//      has been snapshotting for a couple of days.
//   2. Payments vs Terms (recipe: payments-behavior) — how much money comes
//      in before / on / after terms, weekly stacked bars + day-of-week split.
//
// All date strings (points[].date, weeks[].weekStart) are server-formatted —
// rendered as-is, never through new Date().

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle, Info } from 'lucide-react';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { apiV2 as api } from '../../services/apiV2';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import type { ArHistoryPoint, ArHistoryResponse, PaymentsWeek } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtPct = (v: number) => `${v.toFixed(1)}%`;

const GRANULARITIES: { value: string; label: string }[] = [
  { value: 'day',   label: 'Day' },
  { value: 'week',  label: 'Week' },
  { value: 'month', label: 'Month' },
];

const MONTHS_OPTIONS = [3, 6, 12, 24];
// History split (flag analytics.arsalesman, agent 3.20.0+). '' = bucket view.
const SPLITS: { value: string; label: string }[] = [
  { value: '',                 label: 'Aging buckets' },
  { value: 'area',             label: 'Area' },
  { value: 'salesman',         label: 'Salesman' },
  { value: 'customerCategory', label: 'Customer category' },
];
const SPLIT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4', '#ec4899', '#64748b'];
const WEEKS_OPTIONS = [13, 26, 52];

// Aging bucket colors: current → over120, cool → hot.
const AGING_SERIES: { key: keyof ArHistoryPoint; label: string; color: string }[] = [
  { key: 'current', label: 'Current', color: '#10b981' },
  { key: 'over30',  label: '30+',     color: '#3b82f6' },
  { key: 'over60',  label: '60+',     color: '#f59e0b' },
  { key: 'over90',  label: '90+',     color: '#f97316' },
  { key: 'over120', label: '120+',    color: '#ef4444' },
];

const PAY_SERIES: { key: keyof PaymentsWeek; label: string; color: string }[] = [
  { key: 'before',    label: 'Before Terms', color: '#10b981' },
  { key: 'onTime',    label: 'On Terms',     color: '#3b82f6' },
  { key: 'after',     label: 'After Terms',  color: '#ef4444' },
  { key: 'unapplied', label: 'Unapplied',    color: '#64748b' },
];

function exportHistory(points: ArHistoryPoint[], exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<ArHistoryPoint> = [
    ['Date',    (r) => r.date],
    ['Current', (r) => r.current],
    ['30+',     (r) => r.over30],
    ['60+',     (r) => r.over60],
    ['90+',     (r) => r.over90],
    ['120+',    (r) => r.over120],
    ['Total',   (r) => r.total],
  ];
  exporter('ar-history', cols, points);
}

function exportWeeks(weeks: PaymentsWeek[], exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<PaymentsWeek> = [
    ['Week Start',   (r) => r.weekStart],
    ['Before Terms', (r) => r.before],
    ['On Terms',     (r) => r.onTime],
    ['After Terms',  (r) => r.after],
    ['Unapplied',    (r) => r.unapplied],
    ['Total',        (r) => r.total],
  ];
  exporter('payments-behavior', cols, weeks);
}

const tooltipProps = {
  cursor: { fill: '#475569', fillOpacity: 0.15 },
  contentStyle: { backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' },
  labelStyle: { color: '#f1f5f9' },
};

function ErrorBox({ title, error }: { title: string; error: unknown }) {
  return (
    <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
      <AlertTriangle size={16} />
      <div>
        <div className="font-medium">{title}</div>
        <div className="text-xs text-red-300/70 mt-0.5">{(error as Error).message}</div>
      </div>
    </div>
  );
}

export function ArInsightsTab() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Receipt size={20} /> A/R Insights
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Aging history from daily snapshots, plus payment behavior measured against customer terms.
        </p>
      </div>
      <AgingHistoryPanel />
      <PaymentsBehaviorPanel />
    </div>
  );
}

// ── Panel 1: A/R Aging History ───────────────────────────────────

/** History split by area / salesman / category: stacked balance + latest table. */
function SplitHistory({ byDim }: { byDim: NonNullable<ArHistoryResponse['byDim']> }) {
  const { dates, series } = byDim;
  const chartData = dates.map((date, i) => {
    const row: Record<string, string | number> = { date };
    series.forEach(s => { row[s.label] = s.balance[i] ?? 0; });
    return row;
  });
  const last = dates.length - 1;
  const rows = series.map((s, i) => {
    const bal = s.balance[last] ?? 0;
    const o90 = s.over90[last] ?? 0;
    const first = s.balance[0] ?? 0;
    return { label: s.label, color: SPLIT_COLORS[i % SPLIT_COLORS.length], bal, o90, pct: bal ? (o90 / bal) * 100 : 0, change: bal - first };
  });
  return (
    <div className="space-y-4">
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
            <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
            <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
              interval="preserveStartEnd" minTickGap={32} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
              tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`} />
            <Tooltip {...tooltipProps} formatter={(value, name) => [fmtCur(Number(value)), String(name)]} />
            <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span style={{ color: '#94a3b8' }}>{v}</span>} />
            {series.map((s, i) => (
              <Area key={s.label} type="monotone" dataKey={s.label} stackId="split"
                stroke={SPLIT_COLORS[i % SPLIT_COLORS.length]} fill={SPLIT_COLORS[i % SPLIT_COLORS.length]} fillOpacity={0.4} strokeWidth={1.5} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-700/50 text-slate-400">
            <th className="px-3 py-2 text-left font-medium">As of {dates[last]}</th>
            <th className="px-3 py-2 text-right font-medium">Balance</th>
            <th className="px-3 py-2 text-right font-medium">Over 90</th>
            <th className="px-3 py-2 text-right font-medium">% over 90</th>
            <th className="px-3 py-2 text-right font-medium">Change since {dates[0]}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.label} className="border-b border-slate-800/50">
              <td className="px-3 py-1.5 text-white">
                <span className="inline-block w-2 h-2 rounded-sm mr-2 align-middle" style={{ background: r.color }} />{r.label}
              </td>
              <td className="px-3 py-1.5 text-right text-slate-200 tabular-nums">{fmtCur(r.bal)}</td>
              <td className="px-3 py-1.5 text-right text-slate-300 tabular-nums">{fmtCur(r.o90)}</td>
              <td className={`px-3 py-1.5 text-right tabular-nums ${r.pct >= 30 ? 'text-red-400' : r.pct >= 15 ? 'text-amber-400' : 'text-emerald-400'}`}>{fmtPct(r.pct)}</td>
              <td className={`px-3 py-1.5 text-right tabular-nums ${r.change > 0 ? 'text-red-300' : 'text-emerald-300'}`}>
                {r.change > 0 ? '+' : r.change < 0 ? '−' : ''}{fmtCur(Math.abs(r.change))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AgingHistoryPanel() {
  const [granularity, setGranularity] = useState('week');
  const [months, setMonths] = useState(6);
  const [split, setSplit] = useState('');
  const resetFilters = () => { setGranularity('week'); setMonths(6); setSplit(''); };
  const { canSee } = useFeatureAccess();
  const canSplit = canSee('analytics.arsalesman');
  const splitOn = canSplit ? split : '';

  const q = useQuery({
    queryKey: ['analyticsArHistory', granularity, months, splitOn],
    queryFn: () => api.getArHistory({ granularity, months, split: splitOn }),
    staleTime: 60_000,
    retry: 1,
  });

  const points = q.data?.points ?? [];
  const tooFew = q.data != null && q.data.snapshots < 2;
  const byDim = splitOn ? q.data?.byDim ?? null : null;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="mr-1">
          <h3 className="text-sm font-semibold text-white">A/R Aging History</h3>
          <p className="text-[11px] text-slate-500">
            {q.data?.firstSnapshot && q.data?.lastSnapshot
              ? `${q.data.snapshots} snapshots · ${q.data.firstSnapshot} → ${q.data.lastSnapshot}`
              : 'Bucket totals over time'}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Granularity</span>
          <div className="flex items-center gap-1.5">
            {GRANULARITIES.map(g => (
              <button key={g.value} onClick={() => setGranularity(g.value)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  granularity === g.value
                    ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Months</span>
          <select value={months} onChange={e => setMonths(Number(e.target.value))}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {MONTHS_OPTIONS.map(m => <option key={m} value={m}>{m} months</option>)}
          </select>
        </div>
        {canSplit && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Show</span>
            <select value={split} onChange={e => setSplit(e.target.value)}
              className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
              {SPLITS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        )}
        <div className="flex-1" />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => exportHistory(points)}
          disabled={points.length === 0}
          title="Export aging history to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => exportHistory(points, exportXlsx)}
          disabled={points.length === 0}
          title="Export aging history to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && <ErrorBox title="Couldn't load aging history" error={q.error} />}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && (
        tooFew ? (
          <div className="p-4 rounded-md bg-blue-950/30 border border-blue-900/40 text-blue-200 text-sm flex items-center gap-2">
            <Info size={16} />
            <span>History starts accruing from the day the agent begins snapshotting — check back tomorrow.</span>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            {points.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No snapshots in the selected range</div>
            ) : byDim ? (
              <SplitHistory byDim={byDim} />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={points} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      interval="preserveStartEnd" minTickGap={32} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip {...tooltipProps}
                      formatter={(value, name) => [fmtCur(Number(value)), AGING_SERIES.find(s => s.key === name)?.label ?? String(name)]} />
                    <Legend wrapperStyle={{ fontSize: 11 }}
                      formatter={(v) => <span style={{ color: '#94a3b8' }}>{AGING_SERIES.find(s => s.key === v)?.label ?? v}</span>} />
                    {AGING_SERIES.map(s => (
                      <Area key={s.key} type="monotone" dataKey={s.key} stackId="aging"
                        stroke={s.color} fill={s.color} fillOpacity={0.45} strokeWidth={1.5} />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )
      )}
    </section>
  );
}

// ── Panel 2: Payments vs Terms ───────────────────────────────────

// Weekday stack colours (PBI "AR DOW"). Saturday is listed only when it has money.
const DOW_SERIES = [
  { i: 0, key: 'sun', label: 'Sun', color: '#6366f1' },
  { i: 1, key: 'mon', label: 'Mon', color: '#3b82f6' },
  { i: 2, key: 'tue', label: 'Tue', color: '#06b6d4' },
  { i: 3, key: 'wed', label: 'Wed', color: '#10b981' },
  { i: 4, key: 'thu', label: 'Thu', color: '#f59e0b' },
  { i: 5, key: 'fri', label: 'Fri', color: '#f43f5e' },
  { i: 6, key: 'sat', label: 'Sat', color: '#a855f7' },
];

function PaymentsBehaviorPanel() {
  const [weeks, setWeeks] = useState(26);
  const resetFilters = () => { setWeeks(26); };

  const q = useQuery({
    queryKey: ['analyticsPayBehavior', weeks],
    queryFn: () => api.getPaymentsBehavior({ weeks }),
    staleTime: 60_000,
    retry: 1,
  });

  const weekRows = q.data?.weeks ?? [];
  const dow = q.data?.dow ?? [];
  const summary = q.data?.summary;
  // Weekly-by-weekday stack (flag analytics.arfocus; needs byDow from agent 3.20.0+).
  const { canSee } = useFeatureAccess();
  const weeklyDowAvailable = canSee('analytics.arfocus') && weekRows.some(w => w.byDow);
  const [dowMode, setDowMode] = useState<'total' | 'weekly'>('weekly');
  const showWeeklyDow = weeklyDowAvailable && dowMode === 'weekly';
  const dowSeries = DOW_SERIES.filter(d => d.i !== 6 || weekRows.some(w => (w.byDow?.[6] ?? 0) !== 0));
  const weeklyDow = weekRows.map(w => ({
    weekStart: w.weekStart,
    ...Object.fromEntries(dowSeries.map(d => [d.key, w.byDow?.[d.i] ?? 0])),
  }));

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="mr-1">
          <h3 className="text-sm font-semibold text-white">Payments vs Terms</h3>
          <p className="text-[11px] text-slate-500">
            {q.data ? `${q.data.from} → ${q.data.to}` : 'How payments land relative to customer terms'}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Weeks</span>
          <select value={weeks} onChange={e => setWeeks(Number(e.target.value))}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {WEEKS_OPTIONS.map(w => <option key={w} value={w}>{w} weeks</option>)}
          </select>
        </div>
        <div className="flex-1" />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => exportWeeks(weekRows)}
          disabled={weekRows.length === 0}
          title="Export weekly payments split to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => exportWeeks(weekRows, exportXlsx)}
          disabled={weekRows.length === 0}
          title="Export weekly payments split to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && <ErrorBox title="Couldn't load payment behavior" error={q.error} />}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && (
        <>
          {/* Summary tiles */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Total Payments</p>
                <p className="text-lg font-bold text-white tabular-nums">{fmtCur(summary.total)}</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/30">
                <p className="text-xs text-slate-500">On/Before Terms</p>
                <p className="text-lg font-bold text-emerald-300 tabular-nums">{fmtPct(summary.beforePct + summary.onTimePct)}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/30">
                <p className="text-xs text-slate-500">After Terms</p>
                <p className="text-lg font-bold text-red-300 tabular-nums">{fmtPct(summary.afterPct)}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Unapplied</p>
                <p className="text-lg font-bold text-slate-300 tabular-nums">{fmtPct(summary.unappliedPct)}</p>
              </div>
            </div>
          )}

          {/* Weekly stacked bars */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <h4 className="text-sm font-semibold text-white mb-3">Weekly payments by terms bucket</h4>
            {weekRows.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No payments in the selected range</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weekRows} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="weekStart" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      interval="preserveStartEnd" minTickGap={32} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip {...tooltipProps}
                      formatter={(value, name) => [fmtCur(Number(value)), PAY_SERIES.find(s => s.key === name)?.label ?? String(name)]} />
                    <Legend wrapperStyle={{ fontSize: 11 }}
                      formatter={(v) => <span style={{ color: '#94a3b8' }}>{PAY_SERIES.find(s => s.key === v)?.label ?? v}</span>} />
                    {PAY_SERIES.map(s => (
                      <Bar key={s.key} dataKey={s.key} stackId="pay" fill={s.color} maxBarSize={28} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Day-of-week split */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-white">Payments by day of week</h4>
              {weeklyDowAvailable && (
                <div className="flex items-center gap-1">
                  {([['weekly', 'By week'], ['total', 'Totals']] as const).map(([v, label]) => (
                    <button key={v} onClick={() => setDowMode(v)}
                      className={`px-2 py-1 text-[11px] rounded-md border transition-all ${
                        dowMode === v
                          ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
                          : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {showWeeklyDow ? (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyDow} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="weekStart" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v: string) => v.slice(5)} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => `${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip {...tooltipProps} labelFormatter={(l) => `Week of ${l}`} formatter={(value, name) => [fmtCur(Number(value)), String(name)]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {dowSeries.map(d => (
                      <Bar key={d.key} dataKey={d.key} name={d.label} stackId="dow" fill={d.color} maxBarSize={28} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : dow.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">No data</div>
            ) : (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dow} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip {...tooltipProps} formatter={(value) => [fmtCur(Number(value)), 'Payments']} />
                    <Bar dataKey="amount" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
